import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * Merchant-panel glyphs exported from the Noor.Merchant Figma dashboard, cleaned
 * to `currentColor` icons so they inherit color from their container (e.g. the
 * white icon inside `WidgetHeader`'s orange tile). Use like a lucide icon:
 * `<MoneyIcon className="size-6" />`. Some are solid fills (money, bar-chart,
 * chat, star, plus, support), some are strokes (arrow-up-right, chevron) —
 * matching the source marks. Prefer lucide-react for generic glyphs; use these
 * for the design's specific custom marks.
 */
type IconProps = React.ComponentProps<'svg'>;

/** Balance card header — a banknote/cash note (solid). */
function MoneyIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M0 6.02239V18.9307C0 19.6807 0.420833 20.3891 1.125 20.6516C4.75 22.0057 8.375 21.0807 12 20.1557C15.325 19.3099 18.65 18.4599 21.9708 19.3682C22.9292 19.6307 24 18.9724 24 17.9766V5.06406C24 4.31406 23.5792 3.60573 22.875 3.34323C19.25 1.9974 15.625 2.92239 12 3.84739C8.675 4.69323 5.35 5.53906 2.02917 4.63073C1.06667 4.36823 0 5.02656 0 6.02239ZM12 16.0016C10.1583 16.0016 8.66667 14.2099 8.66667 12.0016C8.66667 9.79323 10.1583 8.00156 12 8.00156C13.8417 8.00156 15.3333 9.79323 15.3333 12.0016C15.3333 14.2099 13.8417 16.0016 12 16.0016ZM2.66667 16.0016C4.1375 16.0016 5.33333 17.1974 5.33333 18.6682H2.66667V16.0016ZM5.33333 7.3349C5.33333 8.80573 4.1375 10.0016 2.66667 10.0016V7.3349H5.33333ZM21.3333 14.0016V16.6682H18.6667C18.6667 15.1974 19.8625 14.0016 21.3333 14.0016ZM18.6667 5.33489H21.3333V8.00156C19.8625 8.00156 18.6667 6.80573 18.6667 5.33489Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Balance card "open report" affordance — a 45° up-right arrow (stroke). */
function ArrowUpRightIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M7 7H17V17M17 7L7 17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Busy-days chart header — a three-column bar chart (solid). */
function BarChartIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M21 5C21 3.89543 20.1046 3 19 3H18.5C17.3954 3 16.5 3.89543 16.5 5V20.5C16.5 20.7761 16.7239 21 17 21H19C20.1046 21 21 20.1046 21 19V5Z"
        fill="currentColor"
      />
      <path
        d="M14.5 13.5C14.5 13.2239 14.2761 13 14 13H10C9.72386 13 9.5 13.2239 9.5 13.5V20.5C9.5 20.7761 9.72386 21 10 21H14C14.2761 21 14.5 20.7761 14.5 20.5V13.5Z"
        fill="currentColor"
      />
      <path
        d="M5.5 8C6.60457 8 7.5 8.89543 7.5 10V20.5C7.5 20.7761 7.27614 21 7 21H5C3.89543 21 3 20.1046 3 19V10C3 8.89543 3.89543 8 5 8H5.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Reviews header — a rounded speech bubble (solid). */
function ChatIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M24 11.0828C24 16.4684 18.6284 20.8322 12.0007 20.8322C10.2617 20.8322 8.61183 20.5322 7.12129 19.9932C6.56351 20.401 5.65419 20.9588 4.57612 21.4275C3.45119 21.915 2.09658 22.3321 0.751347 22.3321C0.446677 22.3321 0.174817 22.1493 0.0576367 21.8681C-0.059544 21.5868 0.00607722 21.2681 0.217002 21.0525L0.231064 21.0384C0.245126 21.0244 0.263875 21.0056 0.291998 20.9728C0.343558 20.9166 0.423241 20.8275 0.521672 20.7056C0.713849 20.4713 0.971646 20.1244 1.23413 19.6932C1.70285 18.9151 2.14814 17.8933 2.2372 16.7449C0.831029 15.1513 0.00139 13.1967 0.00139 11.0828C0.00139 5.69714 5.37295 1.33333 12.0007 1.33333C18.6284 1.33333 24 5.69714 24 11.0828Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Review star (14×14 source, solid). Filled uses `currentColor`. */
function StarIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 14 14"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M7.76165 0.477222C7.62114 0.185586 7.3242 0 6.9981 0C6.67199 0 6.37771 0.185586 6.23454 0.477222L4.5298 3.9848L0.722631 4.54686C0.404484 4.59458 0.139361 4.81729 0.041265 5.12218C-0.0568306 5.42707 0.0227063 5.76378 0.250712 5.98913L3.0133 8.72255L2.36109 12.5854C2.30807 12.9035 2.44063 13.227 2.7031 13.4152C2.96557 13.6035 3.31288 13.6273 3.59922 13.4762L7.00075 11.6601L10.4023 13.4762C10.6886 13.6273 11.0359 13.6061 11.2984 13.4152C11.5609 13.2243 11.6934 12.9035 11.6404 12.5854L10.9855 8.72255L13.7481 5.98913C13.9761 5.76378 14.0583 5.42707 13.9576 5.12218C13.8568 4.81729 13.5944 4.59458 13.2762 4.54686L9.46639 3.9848L7.76165 0.477222Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** New-order "+" affordance — a plus (solid). */
function PlusIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M11.5385 1.53846C11.5385 0.6875 10.851 0 10 0C9.14904 0 8.46154 0.6875 8.46154 1.53846V8.46154H1.53846C0.6875 8.46154 0 9.14904 0 10C0 10.851 0.6875 11.5385 1.53846 11.5385H8.46154V18.4615C8.46154 19.3125 9.14904 20 10 20C10.851 20 11.5385 19.3125 11.5385 18.4615V11.5385H18.4615C19.3125 11.5385 20 10.851 20 10C20 9.14904 19.3125 8.46154 18.4615 8.46154H11.5385V1.53846Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** "View all" / row chevron — a right chevron (solid). Source glyph sits in a
 * 50×50 frame at (19,15)–(30,35); the viewBox crops to those bounds. */
function ChevronRightIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="18.5 14.5 12 21"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M30.1372 23.9914C30.695 24.5493 30.695 25.4552 30.1372 26.0131L21.5686 34.5816C21.0108 35.1395 20.1048 35.1395 19.547 34.5816C18.9891 34.0238 18.9891 33.1178 19.547 32.56L27.1069 25L19.5514 17.44C18.9936 16.8822 18.9936 15.9762 19.5514 15.4184C20.1093 14.8605 21.0152 14.8605 21.5731 15.4184L30.1416 23.9869L30.1372 23.9914Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Nav support control — a headset (solid). 18×18 source. */
function SupportIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 18 18"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M9 1.6875C4.96055 1.6875 1.6875 4.96055 1.6875 9V10.4062C1.6875 10.8738 1.31133 11.25 0.84375 11.25C0.376172 11.25 0 10.8738 0 10.4062V9C0 4.02891 4.02891 0 9 0C13.9711 0 18 4.02891 18 9V14.066C18 15.7746 16.6148 17.1598 14.9027 17.1598L11.025 17.1562C10.7332 17.659 10.1883 18 9.5625 18H8.4375C7.50586 18 6.75 17.2441 6.75 16.3125C6.75 15.3809 7.50586 14.625 8.4375 14.625H9.5625C10.1883 14.625 10.7332 14.966 11.025 15.4688L14.9062 15.4723C15.6832 15.4723 16.3125 14.843 16.3125 14.066V9C16.3125 4.96055 13.0395 1.6875 9 1.6875ZM5.0625 7.3125H5.625C6.24727 7.3125 6.75 7.81523 6.75 8.4375V12.375C6.75 12.9973 6.24727 13.5 5.625 13.5H5.0625C3.82148 13.5 2.8125 12.491 2.8125 11.25V9.5625C2.8125 8.32148 3.82148 7.3125 5.0625 7.3125ZM12.9375 7.3125C14.1785 7.3125 15.1875 8.32148 15.1875 9.5625V11.25C15.1875 12.491 14.1785 13.5 12.9375 13.5H12.375C11.7527 13.5 11.25 12.9973 11.25 12.375V8.4375C11.25 7.81523 11.7527 7.3125 12.375 7.3125H12.9375Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * App-switcher trigger — the "apps" grid (Hugeicons interface/outline/apps),
 * exported verbatim from the Noor.Merchant Figma (node 111:4817): two rounded
 * squares (top-left, bottom-right) and two circles (top-right, bottom-left).
 * Stroke glyph, inherits `currentColor`. 24×24 source.
 */
function AppsIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M2 4C2 2.89543 2.89543 2 4 2H8C9.10457 2 10 2.89543 10 4V8C10 9.10457 9.10457 10 8 10H4C2.89543 10 2 9.10457 2 8V4Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M22 6C22 8.20914 20.2091 10 18 10C15.7909 10 14 8.20914 14 6C14 3.79086 15.7909 2 18 2C20.2091 2 22 3.79086 22 6Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M10 18C10 20.2091 8.20914 22 6 22C3.79086 22 2 20.2091 2 18C2 15.7909 3.79086 14 6 14C8.20914 14 10 15.7909 10 18Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M14 16C14 14.8954 14.8954 14 16 14H20C21.1046 14 22 14.8954 22 16V20C22 21.1046 21.1046 22 20 22H16C14.8954 22 14 21.1046 14 20V16Z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

/** Feature bullets — a filled disc with a knocked-out tick (solid). */
function CheckFilledIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 20C15.5228 20 20 15.5228 20 10C20 4.47715 15.5228 0 10 0C4.47715 0 0 4.47715 0 10C0 15.5228 4.47715 20 10 20ZM14.592 7.46049C14.8463 7.13353 14.7874 6.66232 14.4605 6.40802C14.1335 6.15372 13.6623 6.21262 13.408 6.53958L9.40099 11.6914C9.31189 11.806 9.14429 11.8209 9.03641 11.7238L6.50173 9.44256C6.19385 9.16547 5.71963 9.19043 5.44254 9.49831C5.16544 9.80619 5.1904 10.2804 5.49828 10.5575L8.03296 12.8387C8.78809 13.5183 9.9613 13.4143 10.585 12.6123L14.592 7.46049Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Profile card's account-type pill — a faceted diamond/gem (solid, Untitled UI
 * "diamond-01"). Marks the merchant as a legal entity (Юридическое лицо).
 */
function DiamondIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 13.3333 12"
      fill="none"
      className={cn('size-4 shrink-0', className)}
      {...props}
    >
      <path
        d="M5.73955 0.201692C5.79453 0.113333 5.72971 0 5.6242 0H3.93994C3.13419 0 2.38175 0.384136 1.9348 1.02367L0.0675698 3.69546C0.0515274 3.71841 0.0362034 3.74167 0.0215968 3.7652C-0.0407135 3.8656 0.0399275 3.98463 0.159736 3.98463H3.19602C3.31378 3.98463 3.42304 3.92448 3.4844 3.82587L5.73955 0.201692Z"
        fill="currentColor"
      />
      <path
        d="M0.20131 5.31284C0.118502 5.31284 0.068484 5.40076 0.119061 5.46509L5.2359 11.9732C5.28191 12.0317 5.37628 11.9836 5.35395 11.913L3.33957 5.54606C3.29569 5.40737 3.1649 5.31284 3.01688 5.31284H0.20131Z"
        fill="currentColor"
      />
      <path
        d="M7.97889 11.9137C7.95657 11.9842 8.05093 12.0323 8.09694 11.9738L13.2143 5.46509C13.2648 5.40076 13.2148 5.31284 13.132 5.31284H10.3149C10.1669 5.31284 10.0361 5.40739 9.99221 5.54612L7.97889 11.9137Z"
        fill="currentColor"
      />
      <path
        d="M13.1736 3.98463C13.2934 3.98463 13.374 3.8656 13.3117 3.76521C13.2971 3.74167 13.2818 3.71841 13.2658 3.69546L11.3985 1.02367C10.9516 0.384136 10.1991 0 9.39339 0H7.70895C7.60344 0 7.53862 0.113303 7.59357 0.201663L9.84739 3.82579C9.90874 3.92445 10.018 3.98463 10.1358 3.98463H13.1736Z"
        fill="currentColor"
      />
      <path
        d="M6.73104 11.3169C6.71137 11.3791 6.62164 11.3791 6.60196 11.3169L4.77039 5.52772C4.73664 5.42106 4.81789 5.31284 4.93173 5.31284H8.40014C8.51396 5.31284 8.59522 5.42103 8.56149 5.5277L6.73104 11.3169Z"
        fill="currentColor"
      />
      <path
        d="M5.14572 3.73251C5.07699 3.84296 5.15802 3.98463 5.28991 3.98463H8.0422C8.17408 3.98463 8.2551 3.843 8.18642 3.73255L6.81067 1.52035C6.74483 1.41448 6.58814 1.41446 6.52226 1.52032L5.14572 3.73251Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Inline "what is this?" hint beside a card heading — a filled info disc. */
function InformationIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 18 18"
      fill="none"
      className={cn('size-[18px] shrink-0', className)}
      {...props}
    >
      <path
        d="M9 18C11.3869 18 13.6761 17.0518 15.364 15.364C17.0518 13.6761 18 11.3869 18 9C18 6.61305 17.0518 4.32387 15.364 2.63604C13.6761 0.948212 11.3869 0 9 0C6.61305 0 4.32387 0.948212 2.63604 2.63604C0.948212 4.32387 0 6.61305 0 9C0 11.3869 0.948212 13.6761 2.63604 15.364C4.32387 17.0518 6.61305 18 9 18ZM7.59375 11.8125H8.4375V9.5625H7.59375C7.12617 9.5625 6.75 9.18633 6.75 8.71875C6.75 8.25117 7.12617 7.875 7.59375 7.875H9.28125C9.74883 7.875 10.125 8.25117 10.125 8.71875V11.8125H10.4062C10.8738 11.8125 11.25 12.1887 11.25 12.6562C11.25 13.1238 10.8738 13.5 10.4062 13.5H7.59375C7.12617 13.5 6.75 13.1238 6.75 12.6562C6.75 12.1887 7.12617 11.8125 7.59375 11.8125ZM9 4.5C9.29837 4.5 9.58452 4.61853 9.79549 4.82951C10.0065 5.04048 10.125 5.32663 10.125 5.625C10.125 5.92337 10.0065 6.20952 9.79549 6.42049C9.58452 6.63147 9.29837 6.75 9 6.75C8.70163 6.75 8.41548 6.63147 8.20451 6.42049C7.99353 6.20952 7.875 5.92337 7.875 5.625C7.875 5.32663 7.99353 5.04048 8.20451 4.82951C8.41548 4.61853 8.70163 4.5 9 4.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Employees card header — a pair of overlapping figures (solid, "users-01"). */
function UsersGroupIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 18 18"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        d="M10.8574 7.83442C11.5799 6.73304 12.0001 5.41559 12.0001 4C12.0001 2.58441 11.5799 1.26696 10.8574 0.165576C11.2195 0.0578482 11.603 0 12 0C14.2092 0 16 1.79086 16 4C16 6.20914 14.2092 8 12 8C11.603 8 11.2195 7.94215 10.8574 7.83442Z"
        fill="currentColor"
      />
      <path
        d="M13.8741 18C13.9563 17.6804 14.0001 17.3453 14.0001 17V16C14.0001 14.1081 13.3434 12.3696 12.2455 11H13C15.7615 11 18 13.2386 18 16V17C18 17.5523 17.5523 18 17 18H13.8741Z"
        fill="currentColor"
      />
      <path
        d="M7 11H5C2.23858 11 0 13.2386 0 16V17C0 17.5523 0.447716 18 1 18H11C11.5523 18 12 17.5523 12 17V16C12 13.2386 9.76142 11 7 11Z"
        fill="currentColor"
      />
      <path
        d="M6 8C8.20914 8 10 6.20914 10 4C10 1.79086 8.20914 0 6 0C3.79086 0 2 1.79086 2 4C2 6.20914 3.79086 8 6 8Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Reports card header — a document with a folded corner (solid, "file-02"). */
function FileIcon({ className, ...props }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 20"
      fill="none"
      className={cn('size-6 shrink-0', className)}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 20H13C14.6569 20 16 18.6569 16 17V7.24264C16 6.80977 15.9064 6.38655 15.7305 6C15.5831 5.67604 15.3778 5.37784 15.1213 5.12132L10.8787 0.87868C10.6222 0.622156 10.324 0.416885 10 0.26946C9.61345 0.0935523 9.19023 0 8.75736 0H3C1.34315 0 0 1.34315 0 3V17C0 18.6569 1.34315 20 3 20ZM9.1716 2.0898C9.08745 2.05151 9 2.11941 9 2.21187V6.00004C9 6.55233 9.44771 7.00004 10 7.00004H13.788C13.8805 7.00004 13.9485 6.91256 13.9102 6.82837C13.861 6.72035 13.7926 6.62105 13.7071 6.53551L9.46447 2.29287C9.37893 2.20733 9.27962 2.13896 9.1716 2.0898Z"
        fill="currentColor"
      />
    </svg>
  );
}

export {
  MoneyIcon,
  ArrowUpRightIcon,
  BarChartIcon,
  ChatIcon,
  StarIcon,
  PlusIcon,
  ChevronRightIcon,
  SupportIcon,
  AppsIcon,
  CheckFilledIcon,
  DiamondIcon,
  InformationIcon,
  UsersGroupIcon,
  FileIcon,
};
export type { IconProps as MerchantIconProps };
